import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { type ColumnDef, DataTable } from '@/components/common/data-table';
import type { OnFarmImplantMatingAllocation } from '@/services/on-farm-implant';

type NumericKey = Exclude<keyof OnFarmImplantMatingAllocation, 'comments'>;

const NUMERIC_COLUMNS: { key: NumericKey; label: string; width: number }[] = [
  { key: 'priority', label: 'priority', width: 70 },
  { key: 'ivcNumber', label: 'ivcNumber', width: 60 },
  { key: 'cleaved', label: 'cleaved', width: 70 },
  { key: 'labShipped', label: 'labShipped', width: 75 },
  { key: 'heldTransfer', label: 'heldTransfer', width: 75 },
  { key: 'discardedGrade1', label: 'discardedGrade1', width: 65 },
  { key: 'discardedGrade2', label: 'discardedGrade2', width: 65 },
  { key: 'discardedGrade3', label: 'discardedGrade3', width: 65 },
  { key: 'degenerates', label: 'degenerates', width: 65 },
  { key: 'shippedClient', label: 'shippedClient', width: 85 },
  { key: 'togFrozen', label: 'togFrozen', width: 70 },
  { key: 'shippedTog', label: 'shippedTog', width: 75 },
  { key: 'heldFrozen', label: 'heldFrozen', width: 75 },
  { key: 'clientTransfer', label: 'clientTransfer', width: 75 },
  { key: 'clientFrozen', label: 'clientFrozen', width: 75 },
  { key: 'clientDiscarded', label: 'clientDiscarded', width: 75 },
  { key: 'loadedInStraws', label: 'loadedInStraws', width: 95 },
  { key: 'billableShipped', label: 'billableShipped', width: 75 },
];

type MatingAllocationTableProps = {
  matingKey: string;
  matingName: string;
  allocation: OnFarmImplantMatingAllocation | null | undefined;
};

export const MatingAllocationTable = ({
  matingKey,
  matingName,
  allocation,
}: MatingAllocationTableProps) => {
  const { t } = useTranslation();

  const missing = (
    <span
      className="text-muted-foreground"
      title={t('onFarmImplant.fresh.allocation.missing')}
      aria-label={t('onFarmImplant.fresh.allocation.missing')}
    >
      —
    </span>
  );

  const columns = useMemo<ColumnDef<OnFarmImplantMatingAllocation>[]>(
    () => [
      {
        key: 'plate',
        header: t('onFarmImplant.fresh.allocation.plate'),
        width: 60,
        align: 'right',
        render: (row) => row.plate ?? missing,
      },
      {
        key: 'mating',
        header: t('onFarmImplant.fresh.mating'),
        width: 140,
        render: () => (
          <span className="block truncate" title={matingName}>
            {matingName}
          </span>
        ),
      },
      ...NUMERIC_COLUMNS.map(
        ({ key, label, width }): ColumnDef<OnFarmImplantMatingAllocation> => ({
          key,
          header: t(`onFarmImplant.fresh.allocation.${label}`),
          width,
          align: 'right',
          render: (row) => row[key] ?? missing,
        }),
      ),
      {
        key: 'comments',
        header: t('onFarmImplant.fresh.allocation.comments'),
        width: 240,
        render: (row) =>
          row.comments ? (
            <span className="block truncate" title={row.comments}>
              {row.comments}
            </span>
          ) : (
            missing
          ),
      },
    ],
    // `missing` only depends on `t`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, matingName],
  );

  return (
    <DataTable
      rows={allocation ? [allocation] : []}
      columns={columns}
      getRowId={() => matingKey}
      storageKey="on-farm-implant-fresh-allocation-table"
      headerClassName="font-['Poppins',sans-serif] font-medium tracking-normal normal-case"
      fillWidth
      emptyMessage={t('onFarmImplant.fresh.allocation.unavailable')}
    />
  );
};
